/* mo-pdp-anim.js — PDP metaobject section animations.
   Two layers:
   1) ENTRANCE (one-shot, never reverses): directional slide + fade-in scrub in as the section
      scrolls into view and LOCK at their resting position (monotonic max-progress). Reaches
      final state when the section centre meets the viewport centre.
   2) AMBIENT PARALLAX (continuous, both directions) after/under the entrance: text & cards stay
      put; shadow layers drift diagonally along their corner (layering); images get a vertical
      parallax + light zoom. Amplitudes clamped to available headroom so covers never gap.
   Reduced motion (or no JS) → everything renders in its final position. */
(function () {
  if (window.__moPdpScroll) return;
  window.__moPdpScroll = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Entrance offsets [fromX, fromY] px per section-type × breakpoint (d = ≥1000px, m = mobile). */
  var CFG = {
    design:      { d: { image: [-80, 0], card: [80, 0],  shadow: [80, 0]  },
                   m: { image: [0, -26], card: [-22, 22], shadow: [-32, 32] } },
    fabrication: { d: { image: [80, 0],  card: [-80, 0], shadow: [-80, 0] },
                   m: { image: [0, 0],   card: [44, 44],  shadow: [58, 58] } },
    packaging:   { d: { title: [-80, 0], text: [-80, 0], image: [80, 0] },
                   m: { title: [-44, 0], image: [-44, 0], text: [0, 44] } },
    type:        { d: { card: [0, 80],   shadow: [0, 80] },
                   m: {} }
  };

  var SHADOW_LAG = 0.15;   // entrance: shadow trails the card by this much progress
  var OPACITY_AT = 0.26;   // opacity reaches 1 at this progress (faster than the transform)
  var IMG_AMP = 90;        // image vertical parallax amplitude (px, clamped to headroom)
  var IMG_SCALE = 1.3;     // image parallax headroom (bigger = more travel allowed)
  var TYPE_AMP = 44;       // type image vertical parallax amplitude
  var TYPE_SCALE = 1.22;   // type image base headroom
  var TYPE_ZOOM = 0.08;    // type image reveal zoom added on top
  var CARD_AMP = 28;       // text-block vertical parallax (opposite to the image → depth)
  // The shadow follows the CARD's motion exactly, PLUS an equal-x/y diagonal drift toward its own
  // corner. Because the shared card motion is common to both, the shadow's offset RELATIVE to the
  // card is always (±SHADOW_REL·q, ±SHADOW_REL·q) — identical horizontally and vertically at ANY
  // scroll position, so the drop-shadow never goes lopsided.
  var SHADOW_REL_D = 16;   // desktop relative drift
  var SHADOW_REL_M = 12;   // mobile — calmer
  var shadowRel = SHADOW_REL_D;

  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };
  var ease = function (t) { return t * t * (3 - 2 * t); }; // smoothstep

  var items = [];

  function build() {
    var mobile = window.matchMedia('(max-width: 999px)').matches;
    var bp = mobile ? 'm' : 'd';
    shadowRel = mobile ? SHADOW_REL_M : SHADOW_REL_D;
    items = [];
    var sections = document.querySelectorAll('[data-mo-anim]');
    for (var i = 0; i < sections.length; i++) {
      var sec = sections[i];
      var type = sec.getAttribute('data-mo-anim');
      var cfg = (CFG[type] && CFG[type][bp]) || {};
      var els = [];
      for (var role in cfg) {
        if (!Object.prototype.hasOwnProperty.call(cfg, role)) continue;
        var el = sec.querySelector('[data-mo-el="' + role + '"]');
        if (!el) continue;
        var sdx = 1, sdy = 1; // shadow drift direction (toward its resting corner)
        if (role === 'shadow') {
          var tr = getComputedStyle(el).translate;
          if (tr && tr !== 'none') {
            var parts = tr.split(' ');
            var x0 = parseFloat(parts[0]);
            var y0 = parseFloat(parts[1] != null ? parts[1] : parts[0]);
            if (x0 < 0) sdx = -1;
            if (y0 < 0) sdy = -1;
          }
        }
        els.push({
          el: el, fx: cfg[role][0], fy: cfg[role][1], shadow: role === 'shadow', sdx: sdx, sdy: sdy,
          ambient: role === 'card' || role === 'title' || role === 'text' // light vertical parallax
        });
      }
      var par = [];
      var pw = sec.querySelectorAll('.mo-pdp-block__parallax');
      for (var j = 0; j < pw.length; j++) par.push(pw[j]);
      var typeImg = type === 'type' ? sec.querySelector('.mo-pdp-type__media img') : null;
      items.push({ sec: sec, els: els, par: par, typeImg: typeImg, maxP: 0 });
    }
  }

  function apply() {
    var vh = window.innerHeight;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var r = it.sec.getBoundingClientRect();
      if (r.bottom < -160 || r.top > vh + 160) continue;
      var center = r.top + r.height / 2;
      var start = vh + r.height / 2;
      var end = vh / 2;
      var p = clamp((start - center) / (start - end || 1), 0, 1);
      if (p > it.maxP) it.maxP = p;          // one-shot: entrance never reverses
      var mp = it.maxP;
      var em = ease(mp);
      var emS = ease(clamp((mp - SHADOW_LAG) / (1 - SHADOW_LAG), 0, 1));
      var op = clamp(mp / OPACITY_AT, 0, 1);
      var q = clamp((end - center) / vh, -1, 1); // ambient parallax driver (live, bidirectional)

      for (var k = 0; k < it.els.length; k++) {
        var o = it.els[k];
        var tx, ty;
        if (o.shadow) {
          // entrance (one-shot, lagged) + the CARD's own ambient (0, -CARD_AMP·q) + an equal-x/y
          // diagonal drift. The shared card term keeps them moving together; the ∓shadowRel·q term
          // is identical on both axes → the gap to the card stays a clean 45°. Sign is negative so
          // the shadow keeps CONVERGING on scroll-down (matching the entrance) and diverges on
          // scroll-up — no direction reversal at the entrance→ambient hand-off.
          tx = o.fx * (1 - emS) - o.sdx * q * shadowRel;
          ty = o.fy * (1 - emS) - q * CARD_AMP - o.sdy * q * shadowRel;
        } else {
          // entrance (one-shot) + light vertical parallax (opposite to the image) for cards/text
          tx = o.fx * (1 - em);
          ty = o.fy * (1 - em) - (o.ambient ? q * CARD_AMP : 0);
        }
        o.el.style.transform = 'translate(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px)';
        o.el.style.opacity = op.toFixed(3);
      }

      // Image vertical parallax (design/fabrication) — clamped to scale headroom.
      for (var m2 = 0; m2 < it.par.length; m2++) {
        var w = it.par[m2];
        var head = (IMG_SCALE - 1) * w.offsetHeight / 2 - 3;
        var d = clamp(q * IMG_AMP, -head, head);
        w.style.transform = 'translateY(' + d.toFixed(1) + 'px) scale(' + IMG_SCALE + ')';
      }

      // Type image: vertical parallax + reveal zoom (both breakpoints), clamped to headroom.
      if (it.typeImg) {
        var sc = TYPE_SCALE + TYPE_ZOOM * em;
        var th = (sc - 1) * it.typeImg.offsetHeight / 2 - 3;
        var td = clamp(q * TYPE_AMP, -th, th);
        it.typeImg.style.transform = 'translateY(' + td.toFixed(1) + 'px) scale(' + sc.toFixed(4) + ')';
      }
    }
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { apply(); ticking = false; });
  }

  function init() {
    if (reduce) return;
    build();
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      var prev = {};
      for (var i = 0; i < items.length; i++) prev[i] = items[i].maxP;
      build();
      for (var j = 0; j < items.length; j++) if (prev[j] != null) items[j].maxP = prev[j];
      apply();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
